import React from 'react';
import { Link } from 'react-router-dom';

import { Avatar } from '@vyaguta/vyaguta-ui';
import { UiDownloadAlt } from '@vyaguta/vyaguta-icons/ui';

import Empty from 'components/common/empty/Empty';
import Loading from 'components/common/loading/Loading';

import * as routes from 'constants/routes';
import { EMPLOYEES } from 'constants/permissions';

import { useUserPermissions } from 'hooks/query/useUserInfo';
import { extractFullName, getEmpImageUrl, interpolate, pluralize } from 'utils/string';

import type { ResourceEmployeeSkillV1, ResourceEmployeeV1, ResourceFinderInformationSlice } from '../types';

const parseSkillsFrom = (skills: ResourceEmployeeSkillV1[] | null | undefined): React.ReactNode =>
  skills && skills.length ? (
    <div className="d-flex fd-row f-wrap">
      {skills.map((skill) => (
        <div className="selected-skill" key={String(skill.id)}>
          <span>{skill.name}</span>
          {skill.rating ? (
            <div className="skill-rating">
              <span>{String(skill.rating)}</span>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  ) : null;

interface EmployeeRowProps {
  employee: ResourceEmployeeV1;
}

const EmployeeRow: React.FC<EmployeeRowProps> = ({ employee }) => {
  const { id, empId, designation, skills, firstName, lastName, middleName } = employee;

  const nameFields = { firstName, middleName, lastName };

  return (
    <div className="employee-row clearfix">
      <Avatar
        className="left nametag nametag--lg profile-photo people-finder-profile"
        image={getEmpImageUrl(String(empId))}
        alt={extractFullName(nameFields)}
      />
      <div className="left namecard__info">
        <Link className="profile-link text--bold text--grey fs-13" to={interpolate(routes.EMPLOYEE_PROFILE, { id })}>
          {extractFullName(nameFields)}
        </Link>
        <div className="muted-text res-designation-name">{designation.name}</div>
        <div className="mb-20">{parseSkillsFrom(skills)}</div>
      </div>
    </div>
  );
};

export interface ResourceResultsV1Props {
  total: number;
  employees: ResourceEmployeeV1[];
  exporting: boolean;
  onExport: () => void | Promise<void>;
}

const ResourceResultsV1: React.FC<ResourceResultsV1Props> = ({ total, employees, exporting, onExport }) => {
  const permissions = useUserPermissions() as ResourceFinderInformationSlice['value']['permissions'];

  return (
    <>
      <div className="resource-list-header table-header name-wrap">
        <div className="d-flex flex-row">
          <span className="resource-header-title-v1">{'Leapfroggers'}</span>
        </div>
        <div className="d-flex flex-row ai-center">
          <span className="resource-results">{pluralize(`${total || 'No'} Result`, total)}</span>
          {permissions[EMPLOYEES.EXPORT] && !!total && (
            <button
              type="button"
              className="btn btn--outlined-grey export-button"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? (
                <div className="export-button loading">
                  <Loading />
                </div>
              ) : (
                <>
                  <UiDownloadAlt className="download-icon" />
                  {'.CSV'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
      <div className="resource-result-list">
        {employees && employees.length ? (
          employees.map((employee, index) => <EmployeeRow employee={employee} key={index} />)
        ) : (
          <div className="empty-container__wrapper">
            <Empty message={'No leapfroggers found'} />
          </div>
        )}
      </div>
    </>
  );
};

export default ResourceResultsV1;
